import { useCallback, useRef, useState } from "react";
import { useSendConnectionReq } from "../../api/mutations/useSendConnectionReq";
import { useCancelConnectionRequest } from "../../api/mutations/useCancelConnectionRequest";
import { useRespondConnection } from "../../api/mutations/useRespondConnection";
import { useDeleteConnection } from "../../api/mutations/useDeleteConnection";
import { useSendEmail } from "../../api/mutations/useSendEmail";
import { User } from "../../types/Types";
import { toast } from "react-toastify";
import type { TFunction } from "i18next";
import { getConnectionRequestSendErrorMessage } from "../../utils/connectionRequestError";
import {
  getConnectionRequestEmailBody,
  getAcceptanceEmailBody,
} from "../../utils/connectionEmailUtils";

interface UseEventAttendeeConnectionsArgs {
  t: TFunction;
  locale: string;
  userId: string;
  objectId: string | undefined;
  currentUserId: number;
  currentUserName: string;
  weekResetsUtc: string | undefined;
  used: number;
  limit: number;
  guardConnectionAction: () => boolean;
  handleConnectionLimitError: (error: unknown) => boolean;
  refetchExplorerConnectionLimit: () => void;
  refetchConnections: () => void;
  refetchConnectionRequests: () => void;
  refetchSentConnectionRequests: () => void;
}

export function useEventAttendeeConnections({
  t,
  locale,
  userId,
  objectId,
  currentUserId,
  currentUserName,
  weekResetsUtc,
  used,
  limit,
  guardConnectionAction,
  handleConnectionLimitError,
  refetchExplorerConnectionLimit,
  refetchConnections,
  refetchConnectionRequests,
  refetchSentConnectionRequests,
}: UseEventAttendeeConnectionsArgs) {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedForIgnore, setSelectedForIgnore] = useState<number | null>(null);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [connectionToRemove, setConnectionToRemove] = useState<User | null>(null);
  const [sendConnectionId, setSendConnectionId] = useState<number | null>(null);
  const [cancellingId, setCancellingId] = useState<number | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<User | null>(null);
  const lastConnectedUserRef = useRef<User | null>(null);

  const { mutate: sendMail } = useSendEmail({
    onSuccess: () => setSelectedUser(null),
  });

  const { mutate: sendConnectionReq } = useSendConnectionReq({
    onSuccess: () => {
      setSendConnectionId(null);
      toast.success(t("connections.requestSentSuccess"));
      const u = lastConnectedUserRef.current;
      if (u?.email) {
        sendMail({
          email: u.email,
          name: currentUserName,
          subject: `${u.firstName ?? ""} You've received a new connection request`,
          body: getConnectionRequestEmailBody(u.firstName),
        });
      }
      lastConnectedUserRef.current = null;
      refetchSentConnectionRequests();
      refetchExplorerConnectionLimit();
    },
    onError: (error) => {
      setSendConnectionId(null);
      lastConnectedUserRef.current = null;
      if (handleConnectionLimitError(error)) {
        refetchExplorerConnectionLimit();
        return;
      }
      toast.error(
        getConnectionRequestSendErrorMessage(t, error, locale, {
          weekResetsUtc: weekResetsUtc ?? undefined,
          used,
          limit,
        })
      );
    },
  });

  const { mutate: cancelRequest } = useCancelConnectionRequest({
    onSuccess: () => {
      setCancellingId(null);
      toast.success(t("connections.requestCancelled"));
      refetchSentConnectionRequests();
    },
    onError: () => {
      setCancellingId(null);
      toast.error(t("connections.cancelFailed"));
    },
  });

  const { mutate: respondConnection } = useRespondConnection({
    onSuccess: () => {
      if (selectedRequest?.email) {
        sendMail({
          email: selectedRequest.email,
          name: `${selectedRequest.firstName} ${selectedRequest.lastName}`,
          subject: `${selectedRequest.firstName} Your connection request was accepted`,
          body: getAcceptanceEmailBody(selectedRequest.firstName),
        });
      }
      setSelectedRequest(null);
      setSelectedForIgnore(null);
      refetchConnections();
      refetchConnectionRequests();
      refetchExplorerConnectionLimit();
    },
    onError: (error) => {
      if (handleConnectionLimitError(error)) {
        refetchExplorerConnectionLimit();
      }
    },
  });

  const { mutate: deleteConnection } = useDeleteConnection({
    onSuccess: () => {
      toast.success(t("connections.removedSuccess"));
      setShowRemoveModal(false);
      setConnectionToRemove(null);
      refetchConnections();
    },
    onError: () => toast.error(t("connections.removeError")),
  });

  const handleViewProfile = useCallback((user: User) => {
    setSelectedUser(user);
    setShowProfileModal(true);
  }, []);

  const handleConnect = useCallback(
    (user: User) => {
      if (guardConnectionAction()) return;
      setSendConnectionId(user.id);
      lastConnectedUserRef.current = user;
      sendConnectionReq({ userId, receiverId: user.id });
    },
    [userId, sendConnectionReq, guardConnectionAction]
  );

  const handleCancelRequest = useCallback(
    (id: number) => {
      setCancellingId(id);
      if (!objectId) return;
      cancelRequest({ id, objectId });
    },
    [objectId, cancelRequest]
  );

  const handleAccept = useCallback(
    (id: number, req: User) => {
      if (guardConnectionAction()) return;
      respondConnection({
        requestId: id,
        senderId: currentUserId,
        accept: true,
      });
      setSelectedRequest(req);
    },
    [currentUserId, respondConnection, guardConnectionAction]
  );

  const handleIgnore = useCallback((id: number) => setSelectedForIgnore(id), []);

  const handleConfirmIgnore = useCallback(() => {
    if (selectedForIgnore !== null) {
      respondConnection({
        requestId: selectedForIgnore,
        senderId: currentUserId,
        accept: false,
      });
      setSelectedForIgnore(null);
    }
  }, [selectedForIgnore, currentUserId, respondConnection]);

  const handleRemoveConnection = useCallback((user: User) => {
    setConnectionToRemove(user);
    setShowRemoveModal(true);
  }, []);

  const confirmRemove = useCallback(() => {
    if (connectionToRemove && objectId)
      deleteConnection({ objectId, id: connectionToRemove.id });
  }, [connectionToRemove, objectId, deleteConnection]);

  return {
    selectedUser,
    showProfileModal,
    setShowProfileModal,
    selectedForIgnore,
    setSelectedForIgnore,
    showRemoveModal,
    setShowRemoveModal,
    connectionToRemove,
    sendConnectionId,
    cancellingId,
    handleViewProfile,
    handleConnect,
    handleCancelRequest,
    handleAccept,
    handleIgnore,
    handleConfirmIgnore,
    handleRemoveConnection,
    confirmRemove,
  };
}
